import { v4 as uuidv4 } from 'uuid';
import {
  AssignmentRule,
  RuleJSON,
  CreateRuleRequest,
} from 'services/bidAnalysis/bidAnalysisServices';

// ============================================================
// TYPES
// ============================================================

export interface RuleConditionForm {
  field: 'profitPercentage' | 'gpPerUnit';
  operator: '>' | '<' | '>=' | '<=' | '=' | 'between';
  value?: string;
  minValue?: string;
  maxValue?: string;
}

export interface RuleFormState {
  rule_id: string;
  rule_name: string;
  contract_type: 'MAS direct' | 'MAS indirect' | 'NC';
  combinator: 'and' | 'or';
  conditions: RuleConditionForm[];
  special_notes: string;
  attribute_ids: number[];
}

// ============================================================
// CONSTANTS
// ============================================================

export const FIELD_OPTIONS = [
  { label: 'Profit Percentage', value: 'profitPercentage' as const },
  { label: 'GP Per Unit', value: 'gpPerUnit' as const },
];

export const OPERATOR_OPTIONS = [
  { label: '> (Greater than)', value: '>' as const },
  { label: '< (Less than)', value: '<' as const },
  { label: '>= (Greater than or equal)', value: '>=' as const },
  { label: '<= (Less than or equal)', value: '<=' as const },
  { label: '= (Equal to)', value: '=' as const },
  { label: 'Between', value: 'between' as const },
];

export const CONTRACT_TYPE_OPTIONS = [
  { label: 'MAS Direct', value: 'MAS direct' as const },
  { label: 'MAS Indirect', value: 'MAS indirect' as const },
  { label: 'NC (National Contract)', value: 'NC' as const },
];

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Generate a unique rule ID using UUID
 */
export const generateRuleId = (): string => {
  return uuidv4();
};

/**
 * Format rule conditions for display in table
 */
export const formatRuleConditions = (ruleJson: RuleJSON): string => {
  const fieldLabels: Record<string, string> = {
    profitPercentage: 'Profit%',
    gpPerUnit: 'GP/Unit',
  };

  const conditionStrings = ruleJson.rules.map(rule => {
    const fieldLabel = fieldLabels[rule.field];
    if (rule.operator === 'between') {
      return `${fieldLabel} between ${rule.value}`;
    }
    return `${fieldLabel} ${rule.operator} ${rule.value}`;
  });

  return conditionStrings.join(` ${ruleJson.combinator.toUpperCase()} `);
};

/**
 * Convert form state to API payload format
 */
export const convertFormToPayload = (form: RuleFormState): CreateRuleRequest => {
  const rules = form.conditions.map(condition => {
    let value = condition.value || '';
    // For "between", combine min and max into "min-max" format
    if (condition.operator === 'between' && condition.minValue && condition.maxValue) {
      value = `${condition.minValue}-${condition.maxValue}`;
    }
    return {
      field: condition.field,
      operator: condition.operator,
      value: String(value),
    };
  });

  return {
    rule_id: form.rule_id,
    rule_name: form.rule_name,
    rule_json: {
      contract_type: form.contract_type,
      combinator: form.combinator,
      rules,
    },
    special_notes: form.special_notes,
    attribute_ids: form.attribute_ids,
  };
};

/**
 * Convert API rule to form state (for editing)
 */
export const convertRuleToForm = (rule: AssignmentRule): RuleFormState => {
  const conditions = rule.rule_json.rules.map(r => {
    if (r.operator === 'between') {
      const [min, max] = r.value.split('-');
      return {
        field: r.field,
        operator: r.operator,
        minValue: min,
        maxValue: max,
      };
    }
    return {
      field: r.field,
      operator: r.operator,
      value: r.value,
    };
  });

  return {
    rule_id: rule.rule_id,
    rule_name: rule.rule_name,
    contract_type: rule.rule_json.contract_type,
    combinator: rule.rule_json.combinator,
    conditions,
    special_notes: rule.special_notes,
    attribute_ids: rule.attributes.map(a => a.id),
  };
};

/**
 * Create initial empty rule form state
 */
export const createEmptyRuleForm = (): RuleFormState => ({
  rule_id: generateRuleId(),
  rule_name: '',
  contract_type: 'MAS direct',
  combinator: 'and',
  conditions: [{ field: 'profitPercentage', operator: '>', value: '' }],
  special_notes: '',
  attribute_ids: [],
});

// ============================================================
// VALIDATION FUNCTIONS
// ============================================================

/**
 * Validate rule form before submission
 * Returns error message if invalid, null if valid
 */
export const validateRuleForm = (form: RuleFormState): string | null => {
  if (!form.rule_name.trim()) {
    return 'Rule name is required';
  }
  
  if (form.conditions.length === 0) {
    return 'At least one condition is required';
  }
  
  if (form.attribute_ids.length === 0) {
    return 'At least one attribute must be selected';
  }

  // Validate each condition
  for (let i = 0; i < form.conditions.length; i++) {
    const condition = form.conditions[i];
    
    if (condition.operator === 'between') {
      if (!condition.minValue || !condition.maxValue) {
        return `Condition ${i + 1}: Both min and max values are required for "between" operator`;
      }
      const min = parseFloat(condition.minValue);
      const max = parseFloat(condition.maxValue);
      if (isNaN(min) || isNaN(max)) {
        return `Condition ${i + 1}: Min and max values must be valid numbers`;
      }
      if (min >= max) {
        return `Condition ${i + 1}: Min value must be less than max value`;
      }
    } else {
      if (!condition.value) {
        return `Condition ${i + 1}: Value is required`;
      }
      if (isNaN(parseFloat(condition.value))) {
        return `Condition ${i + 1}: Value must be a valid number`;
      }
    }
  }

  return null;
};

/**
 * Update a specific condition in the conditions array
 */
export const updateCondition = (
  conditions: RuleConditionForm[],
  index: number,
  field: keyof RuleConditionForm,
  value: any
): RuleConditionForm[] => {
  const newConditions = [...conditions];
  newConditions[index] = { ...newConditions[index], [field]: value };
  
  // Clear values when operator changes
  if (field === 'operator') {
    if (value === 'between') {
      newConditions[index].value = undefined;
      newConditions[index].minValue = '';
      newConditions[index].maxValue = '';
    } else {
      newConditions[index].minValue = undefined;
      newConditions[index].maxValue = undefined;
      newConditions[index].value = '';
    }
  }
  
  return newConditions;
};

/**
 * Add a new condition to the conditions array
 */
export const addCondition = (conditions: RuleConditionForm[]): RuleConditionForm[] => {
  return [...conditions, { field: 'profitPercentage', operator: '>', value: '' }];
};

/**
 * Remove a condition from the conditions array
 */
export const removeCondition = (conditions: RuleConditionForm[], index: number): RuleConditionForm[] => {
  return conditions.filter((_, i) => i !== index);
};

